import {
  Divider,
  MenuItem,
  Select,
  Skeleton,
  Stack,
  TextField,
  useTheme,
} from "@mui/material";
import CustomPagination from "global/components/CustomTable/CustomPagination";
import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import contactsStyles from "screens/Contacts/Contacts.styles";
import {
  initialCampaignsFilterState,
  socialCampaignStatusOptions,
} from "../CampaignConstants";
import CenteredCardLayout from "layouts/CenteredTableView/CenteredCardLayout";
import {
  debounceEventHandler,
  handleErrorMessage,
  last30DaysFromDate,
  last30DaysToDate,
  validatePageQuery,
  validatePerPageQuery,
  validateSocialCampaignCategoryQuery,
} from "helpers/methods";
import strings from "global/constants/StringConstants";
import SearchIcon from "assets/icons1/search_icon.svg";
import SocialDataList from "./SocialDataList";
import moment from "moment";
import {
  campaignFilters,
  DateRange,
  SocialCampaignDetails,
} from "models/interfaces";
import {
  fetchAllSocialCampaigns,
  fetchSocialCampaignCounts,
} from "../CampaignService";
import CreateCampaignBox from "./CreateCampaignBox";
import DashboardDateRangePicker from "screens/Dashboard/Components/DashboardDateRangePicker";
import CustomDateRangePicker from "global/components/CustomDateRangePicker/CustomDateRangePicker";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 150,
    },
  },
};

interface CustomProps {
  open: boolean;
  setOpen: Function;
}

const SocialTab = (props: CustomProps) => {
  const theme = useTheme();
  const location: any = useLocation();
  const urlParams = new URLSearchParams(useLocation().search);
  const pageFromUrl = validatePageQuery(urlParams.get("page"));
  const rowsPerPageFromUrl = validatePerPageQuery(urlParams.get("rowsPerPage"));
  const campaignStatusFromUrl = validateSocialCampaignCategoryQuery(
    urlParams.get("status")
  );
  const searchFromUrl = urlParams.get("search");
  const [loading, setLoading] = useState<boolean>(false);
  const classes = contactsStyles(theme);
  const [campaigns, setCampaigns] = useState<SocialCampaignDetails[]>([]);
  const [count, setCount] = useState<number>(0);
  const [campaignFilters, setCampaignFilters] = useState<campaignFilters>(
    initialCampaignsFilterState(
      pageFromUrl,
      rowsPerPageFromUrl,
      searchFromUrl ?? "",
      "all",
      campaignStatusFromUrl ?? "All"
    )
  );
  const [openDateRangePicker, setOpenDateRangePicker] =
    useState<boolean>(false);
  const [dateRange, setDateRange] = useState<DateRange>({
    fromDate: null,
    toDate: null,
  });

  useEffect(() => {
    const {
      page,
      rowsPerPage,
      searchText,
      ownerDropdownValue,
      statusDropdownValue,
    } = campaignFilters;
    window.history.replaceState(
      null,
      "",
      `?page=${page}&rowsPerPage=${rowsPerPage}&status=${statusDropdownValue}&category=${ownerDropdownValue}&tab=social`
    );
    getActiveSocialCampaignData(dateRange);
  }, [campaignFilters]);

  const getActiveSocialCampaignData = async (dateRange: DateRange) => {
    const {
      searchText,
      ownerDropdownValue,
      statusDropdownValue,
      page,
      rowsPerPage,
    } = campaignFilters;
    try {
      const body = {
        status: statusDropdownValue,
        userType: ownerDropdownValue,
        startDate: dateRange.fromDate,
        endDate: dateRange.toDate,
        searchTerm: searchText,
        appName: "",
      };
      setLoading(true);
      const [countRes, response] = await Promise.all([
        fetchSocialCampaignCounts(body),
        fetchAllSocialCampaigns(page, rowsPerPage, body),
      ]);
      setCampaigns(response);
      setCount(countRes);
      setLoading(false);
    } catch (error: any) {
      setLoading(false);
      handleErrorMessage(error);
    }
  };

  const handlePageChange = (event: any, newPage: number) => {
    setCampaignFilters({
      ...campaignFilters,
      page: newPage,
    });
  };

  const handleRowsPerPage = (rowPerPage: number) => {
    setCampaignFilters({
      ...campaignFilters,
      rowsPerPage: rowPerPage,
      page: 1,
    });
  };

  const handleStatusDropDownChange = (event: any) => {
    setCampaignFilters({
      ...campaignFilters,
      page: 1,
      statusDropdownValue: event.target.value,
    });
  };

  const handleSearchOnChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newSearchText = event.target.value.trim();
    setCampaignFilters({
      ...campaignFilters,
      page: 1,
      searchText: newSearchText,
    });
  };

  const getCampaignByStatus = () => {
    return (
      <Select
        variant="outlined"
        id="templates_category_dropdown"
        value={campaignFilters.statusDropdownValue}
        onChange={handleStatusDropDownChange}
        sx={classes.dropDown}
        MenuProps={MenuProps}
        disabled={loading}
      >
        {socialCampaignStatusOptions.map((data, index: number) => (
          <MenuItem
            key={index}
            value={data.value}
            sx={{
              ...classes.dropDownMenuItem,
              padding: "12px",
            }}
          >
            {data.label}
          </MenuItem>
        ))}
      </Select>
    );
  };

  const getDatePicker = () => {
    return (
      <CustomDateRangePicker
        dateRange={dateRange}
        openDateRangePicker={openDateRangePicker}
        setOpenDateRangePicker={setOpenDateRangePicker}
        setDateRange={setDateRange}
        helperFunction={getActiveSocialCampaignData}
        disabled={loading}
      />
    );
  };

  const getFilters = () => {
    return (
      <CenteredCardLayout removeLastChildBottomPadding={true}>
        <TextField
          id="campaigns_search_box"
          placeholder="Search Campaigns"
          variant="outlined"
          sx={{
            "& .MuiOutlinedInput-notchedOutline": {
              border: "none !important",
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              border: "none !important",
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              border: "none !important",
            },
            "&.MuiOutlinedInput-root.Mui-focused": {
              border: "none !important",
            },
          }}
          fullWidth
          onChange={debounceEventHandler(
            handleSearchOnChange,
            strings.SEARCH_TIME_OUT
          )}
          InputProps={{
            startAdornment: (
              <Stack mr={1}>
                <img src={SearchIcon} width="30px" height="20px" />
              </Stack>
            ),
          }}
          disabled={loading}
        />
        <Divider />
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          {getCampaignByStatus()}
          {getDatePicker()}
        </Stack>
      </CenteredCardLayout>
    );
  };

  const getSkeleton = () => {
    return [
      <Stack spacing={2}>
        <Skeleton animation="wave" variant="rounded" height={125} />
        <Skeleton animation="wave" variant="rounded" height={125} />
        <Skeleton animation="wave" variant="rounded" height={125} />
      </Stack>,
    ];
  };

  return (
    <Stack spacing={2}>
      {getFilters()}
      {loading ? (
        getSkeleton()
      ) : count > 0 ? (
        <>
          <SocialDataList
            campaignDetails={campaigns}
            setCampaign={setCampaigns}
            refresh={() => getActiveSocialCampaignData(dateRange)}
          />
          {(campaigns?.length ?? 0) >= 0 && (
            <CustomPagination
              count={count ?? 0}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleRowsPerPage}
              page={campaignFilters.page ?? 1}
              showJumpToPageNumber={false}
              rowsPerPage={campaignFilters.rowsPerPage}
              rowsPerPageOptions={[10, 25, 50, 75, 100]}
              shape="rounded"
            />
          )}
        </>
      ) : (
        <CreateCampaignBox isOpen={props.open} setOpen={props.setOpen} />
      )}
    </Stack>
  );
};

export default SocialTab;
